import React from 'react';
import SubstageArea from './SubstageArea';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { orderWorkflowSubstages } from './workflowSubstageOrder';

const StageSection = ({
  stageId,
  isActive,
  applicantsData,
  onDragOver,
  onDrop,
  onOpenApplicantPopup,
  onOpenFilteringSubstagePopup,
  userUID,
  dynamicSubstages = [],
  onRemoveFilterSubstage,
  onOpenSubstagePopup,
  selectedApplicants = [],
  currentJobDepartment = null,
  isOfferApprover,
  pendingApprovals,
  applicantStatusMap = {},
  jobId,
  onSubstageUpdated,
  onSubstageDeleted,
  onMultipleApplicantsRemoved,
}) => {
  const { t } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  // Filter applicants for this stage
  const stageApplicants = applicantsData.filter(applicant => applicant.stageId === stageId);

  // Dynamic substages separated into filtering and others for stage-1
  const filteringSubstage = dynamicSubstages.find(s => s.id === 'filter-applicants');
  const otherDynamicSubstages = dynamicSubstages.filter(s => s.id !== 'filter-applicants');

  const orderedOtherDynamic = orderWorkflowSubstages(otherDynamicSubstages, currentJobDepartment);

  const sharedMenuProps = {
    userUID,
    jobId,
    onSubstageUpdated,
    onSubstageDeleted,
    onMultipleApplicantsRemoved,
  };

  const renderDynamicSubstagesNodes = (orderedList) => orderedList.map((substage) => {
    const substageApplicants = stageApplicants.filter(a => a.substageId === substage.id);
    return (
      <SubstageArea
        key={substage.id}
        substageId={substage.id}
        substageType={substage.type || 'static'}
        substageAssessmentId={substage.link || null}
        title={substage.title}
        icon={substage.icon}
        applicants={substageApplicants}
        applicantCount={substageApplicants.length}
        onDragOver={onDragOver}
        onDrop={(e) => onDrop(e, stageId, substage.id)}
        onOpenApplicantPopup={onOpenApplicantPopup}
        selectedApplicants={selectedApplicants}
        isOfferApprover={isOfferApprover}
        pendingApprovals={pendingApprovals}
        applicantStatusMap={applicantStatusMap}
        isMenuSupported={true}
        {...sharedMenuProps}
      />
    );
  });

  // ----  STAGE SPECIFIC RENDERS  ---- //
  const renderStage1 = () => {
    const nodes = [];

    // 1. All Applicants (static, non-clickable)
    const allApplicants = stageApplicants.filter(a => a.substageId === 'all-applicants-1');
    nodes.push(
      <SubstageArea
        key="all-applicants-1"
        substageId="all-applicants-1"
        substageType="all-applicants"
        title={t('substages.allApplicants')}
        icon="fa-solid fa-people-group"
        applicants={allApplicants}
        applicantCount={allApplicants.length}
        onDragOver={onDragOver}
        onDrop={(e) => onDrop(e, stageId, 'all-applicants-1')}
        onOpenApplicantPopup={onOpenApplicantPopup}
        selectedApplicants={selectedApplicants}
        isOfferApprover={isOfferApprover}
        pendingApprovals={pendingApprovals}
        applicantStatusMap={applicantStatusMap}
        isMenuSupported={true}
        {...sharedMenuProps}
      />
    );

    // 2. Fit Applicants (if enabled)
    if (filteringSubstage) {
      const filterTitle = filteringSubstage.title || t('substages.fitApplicants');
      const fitApplicants = stageApplicants.filter(a => a.substageId === filteringSubstage.id);
      nodes.push(
        <SubstageArea
          key={filteringSubstage.id}
          substageId={filteringSubstage.id}
          substageType="filtering"
          title={filterTitle}
          icon={filteringSubstage.icon}
          applicants={fitApplicants}
          applicantCount={fitApplicants.length}
          onDragOver={onDragOver}
          onDrop={(e) => onDrop(e, stageId, filteringSubstage.id)}
          onOpenApplicantPopup={onOpenApplicantPopup}
          selectedApplicants={selectedApplicants}
          isOfferApprover={isOfferApprover}
          pendingApprovals={pendingApprovals}
          applicantStatusMap={applicantStatusMap}
          isMenuSupported={true}
          {...sharedMenuProps}
        />
      );
    }

    // 3. Other dynamic substages (ordered by timestamp)
    nodes.push(...renderDynamicSubstagesNodes(orderedOtherDynamic));

    // 4. Offer Sent (static) - should appear BEFORE Hired
    const offerSentApplicants = stageApplicants.filter(a => a.substageId === 'offer-sent');
    nodes.push(
      <SubstageArea
        key="offer-sent"
        substageId="offer-sent"
        substageType="offer"
        title={t('substages.offerSent')}
        icon="fa-solid fa-paper-plane"
        applicants={offerSentApplicants}
        applicantCount={offerSentApplicants.length}
        onDragOver={onDragOver}
        onDrop={(e) => onDrop(e, stageId, 'offer-sent')}
        onOpenApplicantPopup={onOpenApplicantPopup}
        selectedApplicants={selectedApplicants}
        isOfferApprover={isOfferApprover}
        pendingApprovals={pendingApprovals}
        applicantStatusMap={applicantStatusMap}
      />
    );

    // 5. Hired substage (confirmation handled in onDrop logic higher up)
    const hiredApplicants = stageApplicants.filter(a => a.substageId === 'hired-applicants');
    nodes.push(
      <SubstageArea
        key="hired-applicants"
        substageId="hired-applicants"
        substageType="hired"
        title={t('substages.hired')}
        icon="fa-solid fa-handshake"
        applicants={hiredApplicants}
        applicantCount={hiredApplicants.length}
        onDragOver={onDragOver}
        onDrop={(e) => onDrop(e, stageId, 'hired-applicants')}
        onOpenApplicantPopup={onOpenApplicantPopup}
        selectedApplicants={selectedApplicants}
        isOfferApprover={isOfferApprover}
        pendingApprovals={pendingApprovals}
        applicantStatusMap={applicantStatusMap}
      />
    );

    // 6. Move to Onboarding (static)
    const moveToOnboardingApplicants = stageApplicants.filter(a => a.substageId === 'move-to-onboarding');
    nodes.push(
      <SubstageArea
        key="move-to-onboarding"
        substageId="move-to-onboarding"
        substageType="onboarding"
        title={t('substages.moveToOnboarding')}
        icon="fa-solid fa-angles-right"
        applicants={moveToOnboardingApplicants}
        applicantCount={moveToOnboardingApplicants.length}
        onDragOver={onDragOver}
        onDrop={(e) => onDrop(e, stageId, 'move-to-onboarding')}
        onOpenApplicantPopup={onOpenApplicantPopup}
        selectedApplicants={selectedApplicants}
        isOfferApprover={isOfferApprover}
        pendingApprovals={pendingApprovals}
        applicantStatusMap={applicantStatusMap}
      />
    );

    return nodes;
  };

  const renderByStage = () => {
    if (stageId === 'stage-1') return renderStage1();
    return null;
  };

  return (
    <div id={stageId} className="stage-content" style={{ display: isActive ? 'flex' : 'none' }}>
      {renderByStage()}
    </div>
  );
};

export default StageSection; 
